// React
import { useState, useRef, useContext } from "react";

import { useTranslation } from "react-i18next";

// Beautiful-dnd
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Icon } from "../../../../components";

// Utility functions, API calls, and hooks
import { useGetResponsiveDimensions } from "../../../hooks/useGetResponsiveDimensions";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useKanbanState, useKanbanDispatch } from "../../../../recruiter/context/KanbanContext";
import { StatePositionContext, DispatchPositionContext } from "../../../context/PositionContext";
import { StateUserContext } from "../../../../context/UserContext";
import { ACCESS_LEVEL, createRoleAccess } from "../../../routeAccess";
import { getAppliedApplicationsFiles } from "../../../../api/endpoints/application";

// Components
import {
  KanbanContainer,
  KanbanContainerWrapper,
  KanbanColumnStack,
  KanbanDroppable,
  KanbanCardSelectAllInput,
  KanbanSection,
  KanbanSectionTitle,
  KanbanSectionTitleAction,
  KanbanSectionTitleRow,
} from "./Kanban.styled";
import KanbanCard from "./KanbanCard/KanbanCard";
import RejectAll from "../RejectAll/RejectAll";
import { Typography } from "../../../../components/Typography";
import { DispatchErrorContext } from "../../../../context/ErrorContext";

const Kanban = ({ title }) => {
  const { t } = useTranslation();
  const errorDispatch = useContext(DispatchErrorContext);
  const { id } = useParams();
  const dimRef = useRef();
  const { elX } = useGetResponsiveDimensions(dimRef);
  const { compactApplications, loadingCompactApplications, isAllSelected, isIndeterminate, selectedIds } = useKanbanState();
  const { updateCompactApplication, toggleColumnSelection, deselect } = useKanbanDispatch();
  const positionState = useContext(StatePositionContext);
  const positionDispatch = useContext(DispatchPositionContext);
  const { user } = useContext(StateUserContext);
  const [activeApplicationId, setActiveApplicationId] = useState(null);
  const [initial, setInitial] = useState(true);
  const [isDownloadingAppliedFiles, setIsDownloadingAppliedFiles] = useState(false);
  const hasSelections = selectedIds.size > 0;
  const { hasAccess } = createRoleAccess(user?.role);
  const canDownloadAppliedFiles = hasAccess(ACCESS_LEVEL.super);

  const columnLabels = {
    applied: t("kanban.titles.applied", "Applied"),
    shortlisted: t("kanban.titles.shortlisted", "Shortlisted"),
    interview: t("kanban.titles.interview", "Interview"),
    personal_rejection: t("kanban.titles.personal_rejection", "Personal rejection"),
    hired: t("kanban.titles.hired", "Hired"),
    to_be_rejected: t("kanban.titles.to_be_rejected", "To be rejected"),
    rejected: t("kanban.titles.rejected", "Rejected"),
  };

  const getSelectableColumnIds = (applications = []) =>
    applications.filter((application) => application.status !== "rejected" && application.substatus !== "to_be_rejected").map((application) => application.id);

  const renderCards = (applications = []) =>
    applications.map((card, index) => (
      <Draggable key={card.id} draggableId={String(card.id)} index={index} isDragDisabled={card.status === "rejected"}>
        {(provided, snapshot) => (
          <div
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
            style={{
              ...provided.draggableProps.style,
              ...(card.status === "rejected" ? { width: "100%" } : {}),
            }}
          >
            <KanbanCard application={card} activeApplicationId={activeApplicationId} setActiveApplicationId={setActiveApplicationId} initial={initial} setInitial={setInitial} />
          </div>
        )}
      </Draggable>
    ));

  const getFilenameFromDisposition = (disposition, fallbackName) => {
    if (!disposition || typeof disposition !== "string") {
      return fallbackName;
    }

    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
    if (asciiMatch?.[1]) {
      return asciiMatch[1];
    }

    return fallbackName;
  };

  const downloadAppliedApplicationsFiles = async () => {
    if (!id || isDownloadingAppliedFiles) {
      return;
    }

    setIsDownloadingAppliedFiles(true);
    try {
      const response = await getAppliedApplicationsFiles({
        selector: id,
      });

      const fileBlob = response?.data instanceof Blob ? response.data : null;
      if (!fileBlob || fileBlob.size === 0) {
        throw new Error("Failed to fetch applied files zip");
      }

      const contentType = String(response?.headers?.["content-type"] || "").toLowerCase();
      if (contentType.includes("application/json") || contentType.includes("text/html")) {
        const errorPayload = await fileBlob.text();
        throw new Error(errorPayload || "Server did not return a ZIP file");
      }

      const zipSignature = new Uint8Array(await fileBlob.slice(0, 4).arrayBuffer());
      const zipSignatures = [
        [0x50, 0x4b, 0x03, 0x04],
        [0x50, 0x4b, 0x05, 0x06],
        [0x50, 0x4b, 0x07, 0x08],
      ];
      const isZipFile = zipSignatures.some((signature) => signature.every((byte, index) => zipSignature[index] === byte));

      if (!isZipFile) {
        throw new Error("Unsupported file format returned by API");
      }

      const filename = getFilenameFromDisposition(response?.headers?.["content-disposition"], `resumes_position_${id}.zip`);
      const fileUrl = window.URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Delay revoke to avoid browser race conditions during file handoff.
      window.setTimeout(() => window.URL.revokeObjectURL(fileUrl), 1000);
    } catch (err) {
      let rawMessage = typeof err?.message === "string" ? err.message : "";
      const errorBlob = err?.response?.data instanceof Blob ? err.response.data : null;

      if (errorBlob) {
        const errorText = await errorBlob.text();

        try {
          const errorData = JSON.parse(errorText);
          rawMessage = errorData?.message || errorData?.error || errorData?.data || rawMessage;
        } catch {
          rawMessage = errorText || rawMessage;
        }
      }

      const safeMessage = rawMessage && !rawMessage.toLowerCase().includes("<html") ? rawMessage : "";

      errorDispatch({
        type: "set_error",
        payload: safeMessage
          ? `${t("component.kanban.error.downloading_applied_files", "Could not download applied CV files. Please try again.")} (${safeMessage})`
          : t("component.kanban.error.downloading_applied_files", "Could not download applied CV files. Please try again."),
      });
    } finally {
      setIsDownloadingAppliedFiles(false);
    }
  };

  const onDragEnd = async (result) => {
    const { draggableId, source, destination } = result;

    // Return early if no destination
    if (!destination) {
      return;
    }

    // Return early if dropped in the same place
    if (source.droppableId === destination.droppableId) {
      // console.log("%c ⚠️ ⚠️ ⚠️ SAME DESTINATION AS SOURCE - NO STATE CHANGE ⚠️ ⚠️ ⚠️ ", "color: yellow; font-weight: bold");
      return;
    }

    // Return early if the destination is the 'rejected' column (Prevents dragging to the 'rejected' column)
    if (result.destination.droppableId === "rejected") {
      errorDispatch({
        type: "set_error",
        payload: t("component.kanban.error.cannot_drag_to_rejected", "You cannot drag applications directly to Rejected."),
      });
      // console.log("%c 🚫 🚫 🚫 CANNOT DRAG TO REJECTED COLUMN - NO STATE CHANGE 🚫 🚫 🚫 ", "color: #FF0000; font-weight: bold");
      return;
    }

    const applicationId = Number(draggableId);
    const newSubstatus = destination.droppableId;

    if (newSubstatus === "to_be_rejected") {
      deselect(applicationId);
    }

    const response = await updateCompactApplication({
      applicationId,
      substatus: newSubstatus,
    });

    if (response?.success && positionState?.selectedApplication?.id === applicationId) {
      await positionDispatch({
        type: "fetch_selected_application",
        payload: {
          id: applicationId,
          substatus: newSubstatus,
          preserveCandidateView: true,
        },
      });
    }

    if (!response?.success) {
      errorDispatch({
        type: "set_error",
        payload: t("component.kanban.error.updating_application", "An error occurred while updating the application status. Please reload the page and try again."),
      });
      // console.log("%c ❌ ❌ ❌ ERROR UPDATING APPLICATION - STATE ROLLED BACK ❌ ❌ ❌ ", "color: #FF0000; font-weight: bold;");
    }
  };

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <KanbanContainer ref={dimRef} width={window.outerWidth - elX}>
          {Object.keys(columnLabels)
            .filter((column) => column !== "personal_rejection")
            .map((column, columnIndex) => (
              <KanbanContainerWrapper key={column}>
                {column === "interview" ? (
                  <>
                    <KanbanColumnStack>
                      <KanbanSection $split $splitPrimary>
                        <KanbanSectionTitle>
                          {columnLabels[column]}
                          {compactApplications[column].length ? (
                            <Typography tag="span" ml={5} lowOpacity>
                              {compactApplications[column].length}
                            </Typography>
                          ) : null}
                        </KanbanSectionTitle>
                        <KanbanCardSelectAllInput
                          type="checkbox"
                          id={`application-selector-${column}`}
                          name={`all-applications-selector`}
                          $visible={hasSelections}
                          disabled={compactApplications[column].length === 0 || getSelectableColumnIds(compactApplications[column]).length === 0}
                          checked={isAllSelected(getSelectableColumnIds(compactApplications[column]))}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = isIndeterminate(getSelectableColumnIds(compactApplications[column]));
                            }
                          }}
                          onChange={() => toggleColumnSelection(getSelectableColumnIds(compactApplications[column]))}
                        />
                        <Droppable droppableId={column}>
                          {(provided, snapshot) => (
                            <KanbanDroppable {...provided.droppableProps} ref={provided.innerRef} isEmpty={compactApplications[column].length === 0} $split $loading={loadingCompactApplications}>
                              <div className={"body-item-scrollable"}>
                                {renderCards(compactApplications[column])}
                                {provided.placeholder}
                              </div>
                            </KanbanDroppable>
                          )}
                        </Droppable>
                      </KanbanSection>
                      <KanbanSection $split $splitSecondary>
                        <KanbanSectionTitle>
                          {columnLabels["personal_rejection"]}
                          {compactApplications.personal_rejection.length ? (
                            <Typography tag="span" ml={5} lowOpacity>
                              {compactApplications.personal_rejection.length}
                            </Typography>
                          ) : null}
                        </KanbanSectionTitle>
                        <KanbanCardSelectAllInput
                          type="checkbox"
                          id={`application-selector-personal_rejection`}
                          name={`all-applications-selector`}
                          $visible={hasSelections}
                          disabled={compactApplications.personal_rejection.length === 0 || getSelectableColumnIds(compactApplications.personal_rejection).length === 0}
                          checked={isAllSelected(getSelectableColumnIds(compactApplications.personal_rejection))}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = isIndeterminate(getSelectableColumnIds(compactApplications.personal_rejection));
                            }
                          }}
                          onChange={() => toggleColumnSelection(getSelectableColumnIds(compactApplications.personal_rejection))}
                        />
                        <Droppable droppableId="personal_rejection">
                          {(provided, snapshot) => (
                            <KanbanDroppable
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              isEmpty={compactApplications.personal_rejection.length === 0}
                              $split
                              $loading={loadingCompactApplications}
                            >
                              <div className={"body-item-scrollable"}>
                                {renderCards(compactApplications.personal_rejection)}
                                {provided.placeholder}
                              </div>
                            </KanbanDroppable>
                          )}
                        </Droppable>
                      </KanbanSection>
                    </KanbanColumnStack>
                  </>
                ) : (
                  <>
                    <KanbanSectionTitleRow>
                      <KanbanSectionTitle>
                        {columnLabels[column]}
                        {compactApplications[column].length ? (
                          <Typography tag="span" ml={5} lowOpacity>
                            {compactApplications[column].length}
                          </Typography>
                        ) : null}
                      </KanbanSectionTitle>
                      {column === "applied" && canDownloadAppliedFiles && (
                        <KanbanSectionTitleAction type="button" onClick={downloadAppliedApplicationsFiles} disabled={isDownloadingAppliedFiles}>
                          {/* {isDownloadingAppliedFiles ? t("common.downloading", "Downloading...") : t("kanban.download_applied_cvs", "Download applied CVs")} */}
                          <Icon icon="cloud_arrow_down_solid_full" mr="8" />
                        </KanbanSectionTitleAction>
                      )}
                    </KanbanSectionTitleRow>
                    {columnIndex !== 4 && columnIndex !== 5 && (
                      <KanbanCardSelectAllInput
                        type="checkbox"
                        id={`application-selector-${column}`}
                        name={`all-applications-selector`}
                        $visible={hasSelections}
                        disabled={compactApplications[column].length === 0 || getSelectableColumnIds(compactApplications[column]).length === 0}
                        checked={isAllSelected(getSelectableColumnIds(compactApplications[column]))}
                        ref={(el) => {
                          if (el) {
                            el.indeterminate = isIndeterminate(getSelectableColumnIds(compactApplications[column]));
                          }
                        }}
                        onChange={() => toggleColumnSelection(getSelectableColumnIds(compactApplications[column]))}
                      />
                    )}
                    {columnIndex === 4 && <RejectAll positionId={id} toBeRejected={compactApplications.to_be_rejected} />}
                    <Droppable droppableId={column}>
                      {(provided, snapshot) => (
                        <KanbanDroppable {...provided.droppableProps} ref={provided.innerRef} isEmpty={compactApplications[column].length === 0} $loading={loadingCompactApplications}>
                          <div className={"body-item-scrollable"}>
                            {renderCards(compactApplications[column])}
                            {provided.placeholder}
                          </div>
                        </KanbanDroppable>
                      )}
                    </Droppable>
                  </>
                )}
              </KanbanContainerWrapper>
            ))}
        </KanbanContainer>
      </DragDropContext>
    </>
  );
};

export default Kanban;
